package com.evoapps.evoshift.domain.schedule.ai

import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.schedule.AssignmentReason
import com.evoapps.evoshift.domain.schedule.DropReason
import com.evoapps.evoshift.domain.schedule.DroppedProposal
import com.evoapps.evoshift.domain.schedule.PreferenceMode
import com.evoapps.evoshift.domain.schedule.ProposedAssignment
import com.evoapps.evoshift.domain.schedule.ScheduleProblem
import com.evoapps.evoshift.domain.schedule.ScheduleReason
import com.evoapps.evoshift.domain.schedule.StaffConstraint
import com.evoapps.evoshift.domain.schedule.clockMinutes
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.math.roundToInt
import kotlin.random.Random

/*
 * SH-90: what the AI step may see of a scheduling problem. The model gets the SAME problem the
 * rules engine solves (AutoScheduler), but pseudonymised:
 * - people are P1, P2, ... in a random order drawn anew for every request, roles R1, ...,
 *   skills K1, ..., shifts S1, ... (in time order);
 * - only numbers, enums, days of the month, weekdays and HH:MM clock times;
 * - absences and expired medical exam / OHS training are merged into "blocked days", so neither
 *   the kind of absence nor the check that ran out can be told.
 * Never in the request: names, user ids, e-mails, phones, pay rates or costs, notes or
 * availability comments, the workplace's name or address, the roster or shift ids, dates of
 * medical exams or OHS training. The [AliasMap] that turns the answer back into people and
 * shifts stays on the device. functions/aiAssign.js accepts exactly this shape and nothing else.
 */

/** Caps the server enforces too (functions/aiAssign.js LIMITS); a bigger month is not sent. */
object AiScheduleLimits {
    const val MAX_PEOPLE = 80
    const val MAX_SHIFTS = 600
    const val MAX_BUSY = 2000
    const val MAX_RULES_PER_PERSON = 12
    const val MAX_SKILLS = 10
    /** Context shifts this many days before day 1 or after the last day are left out. */
    const val CONTEXT_DAYS = 14
}

/** The JSON the request is sent as and the answer read from (nulls left out). */
val AiScheduleJson: Json = Json {
    encodeDefaults = true
    explicitNulls = false
    ignoreUnknownKeys = true
}

@Serializable
data class AiScheduleRequest(
    val month: AiMonth,
    val range: AiRange,
    val rules: AiRules,
    val shifts: List<AiShift>,
    val people: List<AiPerson>,
    val busy: List<AiBusy>
)

/** The request exactly as it is sent (the `problem` of the callable's data). */
fun AiScheduleRequest.toJson(): String = AiScheduleJson.encodeToString(AiScheduleRequest.serializer(), this)

/** The roster's month: [firstWeekday] is the ISO weekday of day 1 (1 = Monday). */
@Serializable
data class AiMonth(val year: Int, val month: Int, val days: Int, val firstWeekday: Int)

/** The days being planned, as days of the month (both inclusive). */
@Serializable
data class AiRange(val from: Int, val to: Int)

/** The owner's scheduling rules as settings (ScheduleOptions): numbers and enums only. */
@Serializable
data class AiRules(
    val minRestHours: Int,
    val maxConsecutiveDays: Int,
    val preferredRunMin: Int,
    val preferredRunMax: Int,
    val maxIdleDays: Int,
    val maxShiftsPerDay: Int,
    val requireShiftLead: Boolean,
    val requireNonLead: Boolean,
    val ownerTakesMornings: Boolean,
    val morningEndsAt: String,
    val preferenceMode: PreferenceMode,
    val requireSubmittedAvailability: Boolean,
    val maxHoursOverTarget: Double,
    val firstDayOfWeek: Int
)

/**
 * A shift of the month. [open]: to be filled; otherwise [person] holds it (null: someone not on
 * the team being planned). [end] is on the next day when [endsNextDay].
 */
@Serializable
data class AiShift(
    val id: String,
    val day: Int,
    val weekday: Int,
    val start: String,
    val end: String,
    val endsNextDay: Boolean,
    val role: String? = null,
    val skills: List<String> = emptyList(),
    val open: Boolean,
    val person: String? = null
)

@Serializable
data class AiPerson(
    val id: String,
    val role: String? = null,
    val skills: List<String> = emptyList(),
    val owner: Boolean,
    val lead: Boolean,
    val needsSupervision: Boolean,
    val contract: SystemContractType,
    /** Hours this month (null: unknown, no cap). */
    val targetHours: Double? = null,
    /** Hours already held this month (kept assignments). */
    val plannedHours: Double,
    val availabilitySubmitted: Boolean,
    val availability: List<AiDay>,
    /** Days of the month they may not work: absences and expired staff checks, merged. */
    val blockedDays: List<Int>,
    val rules: List<AiRule>
)

/** One day's availability; the preferred slot as its window, when one was chosen. */
@Serializable
data class AiDay(val day: Int, val status: AvailabilityStatus, val slotStart: String? = null, val slotEnd: String? = null)

/** A personal hard rule (StaffConstraint) without its note. */
@Serializable
data class AiRule(
    val kind: AiRuleKind,
    val value: Double? = null,
    val from: String? = null,
    val to: String? = null,
    val days: List<Int>? = null
)

@Serializable
enum class AiRuleKind {
    MAX_DAYS_PER_WEEK, MAX_HOURS_PER_WEEK, MAX_HOURS_PER_MONTH, MAX_DAYS_PER_MONTH, MAX_CONSECUTIVE_DAYS,
    MIN_REST_HOURS, START_BETWEEN, END_BY, NOT_ON_WEEKDAYS, ONLY_ON_WEEKDAYS, NOT_ON_DAYS
}

/**
 * A person's shift outside this roster (neighbouring months, other workplaces): it blocks
 * overlaps and counts for rest and days in a row. [day] is relative to the roster's month (0 is
 * the last day of the previous month, days + 1 the first of the next).
 */
@Serializable
data class AiBusy(val person: String, val day: Int, val start: String, val end: String, val endsNextDay: Boolean, val here: Boolean)

// --- The answer -------------------------------------------------------------------------------

@Serializable
data class AiScheduleAnswer(
    val assignments: List<AiAnswerAssignment> = emptyList(),
    val unfilled: List<AiAnswerUnfilled> = emptyList()
)

@Serializable
data class AiAnswerAssignment(val shift: String, val person: String, val reason: String? = null)

@Serializable
data class AiAnswerUnfilled(val shift: String, val reason: String? = null)

/**
 * Turns aliases back into shift and user ids. Lives only on the device: it is never part of
 * [AiScheduleRequest].
 */
class AliasMap internal constructor(
    private val people: Map<String, String>,
    private val shifts: Map<String, String>
) {
    fun userId(alias: String): String? = people[alias]
    fun shiftId(alias: String): String? = shifts[alias]
    val personAliases: Set<String> get() = people.keys
    val shiftAliases: Set<String> get() = shifts.keys

    /**
     * The answer as proposals for the rules engine, and what was dropped because it named an
     * alias that was not in the request. Reasons outside [AssignmentReason] become null.
     */
    fun resolve(answer: AiScheduleAnswer): Pair<List<ProposedAssignment>, List<DroppedProposal>> {
        val proposals = mutableListOf<ProposedAssignment>()
        val dropped = mutableListOf<DroppedProposal>()
        answer.assignments.forEach { a ->
            val shiftId = shifts[a.shift]
            val userId = people[a.person]
            if (shiftId == null || userId == null) {
                dropped += DroppedProposal(shiftId, userId, DropReason.UNKNOWN_ALIAS)
            } else {
                proposals += ProposedAssignment(shiftId, userId, AssignmentReason.entries.firstOrNull { it.name == a.reason })
            }
        }
        return proposals to dropped
    }
}

/** The request for the model and the device-only way back. */
class AnonymisedScheduleProblem(val request: AiScheduleRequest, val aliases: AliasMap) {

    fun requestJson(): String = request.toJson()

    companion object {
        /** The answer reasons the server accepts for an unfilled shift (ScheduleReason). */
        val UNFILLED_REASONS: List<String> = ScheduleReason.entries.map { it.name }

        /**
         * Pseudonymises [problem]. [random] shuffles the aliases (inject a seeded one in tests);
         * a new draw for every request means P1 is nobody in particular.
         * @throws AiAssignException TOO_LARGE when the month exceeds [AiScheduleLimits].
         */
        fun build(problem: ScheduleProblem, random: Random = Random.Default): AnonymisedScheduleProblem {
            val monthStart = LocalDate(problem.year, problem.month, 1)
            val monthStartEpoch = monthStart.toEpochDays()
            val days = (monthStart.plus(DatePeriod(months = 1)).toEpochDays() - monthStartEpoch).toInt()
            val monthPrefix = "${problem.year}-${problem.month.toString().padStart(2, '0')}"
            fun dayOf(isoDate: String): Int? = runCatching { (LocalDate.parse(isoDate.take(10)).toEpochDays() - monthStartEpoch).toInt() + 1 }.getOrNull()
            fun weekday(epoch: Long): Int = (epoch + 3).mod(7L).toInt() + 1

            val o = problem.options
            val staff = problem.staff.sortedBy { it.userId }.shuffled(random)
            val personAlias = staff.mapIndexed { i, p -> p.userId to "P${i + 1}" }.toMap()
            val roleAlias = (staff.mapNotNull { it.role } + problem.shifts.mapNotNull { it.requiredRole })
                .filter { it.isNotBlank() }.distinct().sorted().shuffled(random)
                .mapIndexed { i, r -> r to "R${i + 1}" }.toMap()
            val skillAlias = (staff.flatMap { it.skills } + problem.shifts.flatMap { it.requiredSkills })
                .distinct().sorted().shuffled(random)
                .mapIndexed { i, k -> k to "K${i + 1}" }.toMap()

            // Shifts in time order: the same order AutoScheduler reads them.
            val readable = problem.shifts
                .sortedWith(compareBy({ it.plannedTime.start }, { it.plannedTime.end }, { it.id }))
                .mapNotNull { shift -> times(shift.plannedTime.start, shift.plannedTime.end)?.let { shift to it } }
            val shiftAlias = readable.mapIndexed { i, (shift, _) -> shift.id to "S${i + 1}" }.toMap()
            val inRange = { date: String -> date in problem.range }
            val aiShifts = readable.mapNotNull { (shift, t) ->
                val day = dayOf(shift.date) ?: return@mapNotNull null
                val clocked = shift.actualTime?.start?.isNotEmpty() == true
                val open = inRange(shift.date) && (shift.userId == null || (o.replan && !clocked))
                AiShift(
                    id = shiftAlias.getValue(shift.id), day = day,
                    weekday = weekday(monthStartEpoch + day - 1),
                    start = t.start, end = t.end, endsNextDay = t.nextDay,
                    role = shift.requiredRole?.takeIf { it.isNotBlank() }?.let { roleAlias[it] },
                    skills = shift.requiredSkills.mapNotNull { skillAlias[it] }.sorted(),
                    open = open,
                    person = if (open) null else shift.userId?.let { personAlias[it] }
                )
            }

            val slots = problem.availabilitySlots.associateBy { it.id }
            val people = staff.map { p ->
                val held = readable.filter { (shift, _) ->
                    shift.userId == p.userId && shift.date.startsWith(monthPrefix) &&
                        !(inRange(shift.date) && (o.replan && shift.actualTime?.start.isNullOrEmpty()))
                }.sumOf { (_, t) -> t.minutes } / 60.0
                val blocked = buildSet {
                    p.absentDates.mapNotNullTo(this) { dayOf(it) }
                    p.staffChecksValidThrough?.let { through ->
                        (1..days).filter { d -> "$monthPrefix-${d.toString().padStart(2, '0')}" > through }.forEach { add(it) }
                    }
                }.filter { it in 1..days }.sorted()
                AiPerson(
                    id = personAlias.getValue(p.userId),
                    role = p.role?.takeIf { it.isNotBlank() }?.let { roleAlias[it] },
                    skills = p.skills.mapNotNull { skillAlias[it] }.sorted(),
                    owner = p.isOwner,
                    lead = p.leadsShifts || p.isOwner,
                    needsSupervision = p.needsSupervision && !(p.leadsShifts || p.isOwner),
                    contract = p.contract,
                    targetHours = p.target.hours?.let(::round2),
                    plannedHours = round2(held),
                    availabilitySubmitted = p.availabilitySubmitted,
                    availability = if (o.assumesAvailable(p)) {
                        // SH-221: the first roster counts someone without answers as available every
                        // day (blockedDays still holds their absences); said so without a new field.
                        (1..days).filter { it !in blocked }.map { AiDay(it, AvailabilityStatus.AVAILABLE) }
                    } else p.availability.entries.mapNotNull { (date, pref) ->
                        val day = dayOf(date)?.takeIf { it in 1..days } ?: return@mapNotNull null
                        val slot = pref.preferredSlotId?.let { slots[it] }
                        AiDay(day, pref.status, slot?.let { clock(it.startTime) }, slot?.let { clock(it.endTime) })
                    }.sortedBy { it.day },
                    blockedDays = blocked,
                    rules = p.constraints.mapNotNull { rule(it, ::dayOf, days) }
                )
            }

            val busy = problem.contextShifts.mapNotNull { c ->
                val alias = personAlias[c.userId] ?: return@mapNotNull null
                val day = dayOf(c.date) ?: return@mapNotNull null
                if (day < 1 - AiScheduleLimits.CONTEXT_DAYS || day > days + AiScheduleLimits.CONTEXT_DAYS) return@mapNotNull null
                val t = times(c.plannedTime.start, c.plannedTime.end) ?: return@mapNotNull null
                AiBusy(alias, day, t.start, t.end, t.nextDay, c.atThisWorkplace)
            }.sortedWith(compareBy({ it.day }, { it.start }, { it.person }))

            if (people.size > AiScheduleLimits.MAX_PEOPLE || aiShifts.size > AiScheduleLimits.MAX_SHIFTS ||
                busy.size > AiScheduleLimits.MAX_BUSY || roleAlias.size > AiScheduleLimits.MAX_PEOPLE ||
                skillAlias.size > AiScheduleLimits.MAX_SKILLS * 4 ||
                people.any { it.rules.size > AiScheduleLimits.MAX_RULES_PER_PERSON || it.skills.size > AiScheduleLimits.MAX_SKILLS }
            ) {
                throw AiAssignException(AiAssignFailure.TOO_LARGE)
            }

            val request = AiScheduleRequest(
                month = AiMonth(problem.year, problem.month, days, weekday(monthStartEpoch)),
                range = AiRange(dayOf(problem.range.start.toString())!!.coerceIn(1, days), dayOf(problem.range.endInclusive.toString())!!.coerceIn(1, days)),
                rules = AiRules(
                    minRestHours = o.effectiveRestHours,
                    maxConsecutiveDays = o.maxConsecutiveDays,
                    preferredRunMin = o.preferredRunDays.first,
                    preferredRunMax = o.preferredRunDays.last,
                    maxIdleDays = o.maxIdleDays,
                    maxShiftsPerDay = o.maxShiftsPerDay,
                    requireShiftLead = o.requireShiftLead,
                    requireNonLead = o.requireNonLead,
                    ownerTakesMornings = o.ownerTakesMornings,
                    morningEndsAt = clock(o.morningEndsAt),
                    preferenceMode = o.preferenceMode,
                    requireSubmittedAvailability = o.requireSubmittedAvailability,
                    maxHoursOverTarget = round2(o.maxHoursOverTarget),
                    firstDayOfWeek = o.firstDayOfWeek
                ),
                shifts = aiShifts,
                people = people.sortedBy { it.id.drop(1).toInt() },
                busy = busy
            )
            return AnonymisedScheduleProblem(
                request,
                AliasMap(personAlias.entries.associate { (uid, alias) -> alias to uid }, shiftAlias.entries.associate { (id, alias) -> alias to id })
            )
        }

        private class Times(val start: String, val end: String, val nextDay: Boolean, val minutes: Int)

        /** "HH:MM" start and end of local ISO timestamps; null when unreadable or not 1–24 h long. */
        private fun times(startIso: String, endIso: String): Times? = runCatching {
            val startDay = LocalDate.parse(startIso.take(10)).toEpochDays()
            val endDay = LocalDate.parse(endIso.take(10)).toEpochDays()
            val start = clockMinutes(startIso.substring(11, 16))
            val end = clockMinutes(endIso.substring(11, 16))
            val minutes = ((endDay - startDay) * 1440 + end - start).toInt()
            if (endDay - startDay !in 0..1 || minutes <= 0 || minutes > 24 * 60) return null
            Times(clock(startIso.substring(11, 16)), clock(endIso.substring(11, 16)), endDay > startDay, minutes)
        }.getOrNull()

        /** "H:mm" / "HH:mm" as "HH:MM" ("24:00" stays). */
        internal fun clock(time: String): String {
            val m = clockMinutes(time).coerceIn(0, 24 * 60)
            return "${(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}"
        }

        private fun round2(value: Double): Double = (value * 100).roundToInt() / 100.0

        private fun rule(c: StaffConstraint, dayOf: (String) -> Int?, days: Int): AiRule? = when (c) {
            is StaffConstraint.MaxDaysPerWeek -> AiRule(AiRuleKind.MAX_DAYS_PER_WEEK, value = c.days.toDouble())
            is StaffConstraint.MaxHoursPerWeek -> AiRule(AiRuleKind.MAX_HOURS_PER_WEEK, value = round2(c.hours))
            is StaffConstraint.MaxHoursPerMonth -> AiRule(AiRuleKind.MAX_HOURS_PER_MONTH, value = round2(c.hours))
            is StaffConstraint.MaxDaysPerMonth -> AiRule(AiRuleKind.MAX_DAYS_PER_MONTH, value = c.days.toDouble())
            is StaffConstraint.MaxConsecutiveDays -> AiRule(AiRuleKind.MAX_CONSECUTIVE_DAYS, value = c.days.toDouble())
            is StaffConstraint.MinRestHours -> AiRule(AiRuleKind.MIN_REST_HOURS, value = c.hours.toDouble())
            is StaffConstraint.StartBetween -> AiRule(AiRuleKind.START_BETWEEN, from = clock(c.earliest), to = clock(c.latestExclusive))
            is StaffConstraint.EndBy -> AiRule(AiRuleKind.END_BY, to = clock(c.latestEnd))
            is StaffConstraint.NotOnDaysOfWeek -> AiRule(AiRuleKind.NOT_ON_WEEKDAYS, days = c.isoDays.filter { it in 1..7 }.sorted())
            is StaffConstraint.OnlyOnDaysOfWeek -> AiRule(AiRuleKind.ONLY_ON_WEEKDAYS, days = c.isoDays.filter { it in 1..7 }.sorted())
            is StaffConstraint.NotOnDates -> c.dates.mapNotNull(dayOf).filter { it in 1..days }.sorted()
                .takeIf { it.isNotEmpty() }?.let { AiRule(AiRuleKind.NOT_ON_DAYS, days = it) }
        }
    }
}
